import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

const errToString = (d) => {
  if (!d) return "Login failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(d);
};

const AdminLoginPage = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-login", { email, password });
      setUser(data);
      toast.success("Welcome to the admin portal");
      const role = data?.role;
      if (role === "cashier") navigate("/cashier");
      else if (role === "production") navigate("/production");
      else if (role === "releasing") navigate("/releasing");
      else navigate(next);
    } catch (e) {
      toast.error(errToString(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="" className="h-20 w-20 rounded-xl object-contain bg-black mx-auto ring-1 ring-white/10" />
          <h1 className="font-display text-3xl font-extrabold text-white mt-5">Admin Portal</h1>
          <p className="text-neutral-400 mt-2 text-sm flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-[#06B6D4]" /> Staff login only
          </p>
        </div>

        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-7">
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-400 block mb-1.5">Email</label>
              <input
                data-testid="admin-login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black border border-neutral-800 rounded-lg px-4 py-3 text-white focus:border-[#06B6D4] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 transition"
                placeholder="you@printad.ph"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-400 block mb-1.5">Password</label>
              <input
                data-testid="admin-login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black border border-neutral-800 rounded-lg px-4 py-3 text-white focus:border-[#06B6D4] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 transition"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full bg-[#06B6D4] hover:bg-[#0891B2] text-white font-bold py-3 rounded-lg transition inline-flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in to admin
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/login" className="text-sm text-neutral-500 hover:text-white" data-testid="admin-login-customer-link">
            ← Customer login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
